import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import {
  useAddRoutes,
  useGetAssignedRoutes,
  useGetRouteDetails,
} from "../../hooks";
import { Checkbox } from "@shared/ui/checkbox";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";

const CreateRoute = ({
  entityName,
  openCreateRoute,
  setOpenCreateRoute,
  meterCycleId,
}: {
  entityName: string;
  openCreateRoute: boolean;
  setOpenCreateRoute: (value: boolean) => void;
  meterCycleId: string;
}) => {
  const { cycle_id } = useParams();
  const utilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const { data: routeList } = useGetAssignedRoutes(
    currentPage,
    10,
    entityName === "Read Cycle" ? cycle_id : meterCycleId,
    undefined,
    searchQuery,
    1
  );

  const { mutateAsync: addRoutes } = useAddRoutes(meterCycleId);

  const routeDetails = routeList?.results;
  const allSelected = useMemo(() => {
    return (
      selectedRoutes.length === routeDetails?.length && routeDetails?.length > 0
    );
  }, [selectedRoutes.length, routeDetails?.length]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      if (selectedRoutes.length !== routeDetails?.length) {
        setSelectedRoutes(routeDetails?.map((route) => route.id));
      }
    } else {
      if (selectedRoutes.length > 0) {
        setSelectedRoutes([]);
      }
    }
  };

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = routeList?.count || 0;
    const totalPages = Math.ceil(totalItems / 10);
    const startItem = (currentPage - 1) * 10 + 1;
    const endItem = Math.min(currentPage * 10, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: 10,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [routeList?.count, currentPage]);

  const handleSelectRoute = (id: string, checked: boolean) => {
    setSelectedRoutes((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      } else {
        if (!prev.includes(id)) return prev;
        return prev.filter((routeId) => routeId !== id);
      }
    });
  };

  const columns: TableColumn<any>[] = [
    {
      key: "select",
      header: (
        <Checkbox
          checked={allSelected}
          onCheckedChange={(checked) => handleSelectAll(!!checked)}
        />
      ),
      width: "w-5",
      type: "custom",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={selectedRoutes.includes(row.id)}
            onCheckedChange={(checked) => handleSelectRoute(row.id, !!checked)}
          />
        </div>
      ),
    },
    {
      key: "routeName",
      header: "Route Name",
      sortable: true,
    },
    {
      key: "meterTypeDisplay",
      header: "Read Type",
      sortable: true,
    },
    {
      key: "utilityService",
      header: "Utility Service",
      sortable: true,
      render: (row) =>
        row?.map((service: any, index: number) => (
          <span key={index}>
            {service}
            {index < row.length - 1 && ", "}
          </span>
        )),
    },
    {
      key: "meterCount",
      header: "Meters",
      sortable: true,
    },
    {
      key: "premiseCount",
      header: "Premises",
      sortable: true,
    },
    {
      key: "areaName",
      header: "Areas",
      sortable: true,
      render: (row) => (
        <>
          {row?.map((area: any, index: number) => (
            <span key={index}>
              {area}
              {index < row.length - 1 && ", "}
            </span>
          ))}
        </>
      ),
    },
    {
      key: "subAreaName",
      header: "Sub Areas",
      sortable: true,
      render: (row) => (
        <>
          {row?.map((subArea: any, index: number) => (
            <span key={index}>
              {subArea}
              {index < row.length - 1 && ", "}
            </span>
          ))}
        </>
      ),
    },
    {
      key: "premiseCount",
      header: "Premises",
      sortable: true,
    },
    {
      key: "meterCount",
      header: "Meters",
      sortable: true,
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleSubmit = () => {
    addRoutes({
      remote_utility_id: utilityId,
      meter_cycle_id: Number(meterCycleId),
      routes: selectedRoutes,
    });
    setOpenCreateRoute(false);
    setSelectedRoutes([]);
  };

  return (
    <Dialog open={openCreateRoute} onOpenChange={setOpenCreateRoute}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Add routes to this read cycle
          </DialogTitle>
          <DialogDescription>
            Search and filter available routes. Select one or more to add to
            this read cycle.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <GenericTable
            data={routeDetails || []}
            columns={columns}
            loading={false}
            searchPlaceholder="Search routes..."
            searchParamName="entityQuery"
            onSearch={handleSearch}
            pagination={pagination}
            onPageChange={handlePageChange}
            searchDebounceMs={300}
            rowKey="id"
            emptyMessage={`No data found`}
          />
        </div>
        <DialogFooter>
          <Button onClick={() => handleSubmit()}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRoute;
